import { useState } from 'react';
import { API_URL } from '@constants';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useData } from '@hooks/useData';

export type RegistrationCarInfoStepInput = {
    brand: string;
    model: string;
    color: string;
    plate: string;
    seats: number;
};

export type RegistrationCarInfoStepSuccessResponse = {
    success: boolean;
    message: string;
    data: {
        uuid: string;
        brand: string;
        model: string;
        color: string;
        plate: string;
        seats: number;
    };
};

export const useRegistrationCarInfoStep = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { draft, setCarInfoStep } = useData();
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const request = async (props: RegistrationCarInfoStepInput) => {
        if (!draft?.uuid) {
            setErrorMessage('Missing UUID from draft');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);
        setErrorMessage(null);
        setValidationErrors(null);

        try {
            const response = await fetch(
                `${API_URL}/draft-registration/step-2/${draft.uuid}`,
                {
                    method: 'POST',
                    headers: getHeaders(),
                    body: JSON.stringify({ ...getCommonParameters(), ...props }),
                }
            );

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                } catch (error: any) {
                    error.message = '';
                }

                error.statusCode = response.status;
                throw error;
            }

            const data = await response.json();
            setSuccess(data.message);
            setCarInfoStep(data as RegistrationCarInfoStepSuccessResponse);

        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setError(error.message);
            error.errors && setValidationErrors(error.errors);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return {
        request,
        loading,
        success,
        error,
        errorMessage,
        validationErrors,
        lastResponseTime
    };
};
